import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { ExportOptions } from "../types/types";

export interface ExcelExportData {
  [key: string]: any;
}

// Функция для группировки данных по категориям
const groupDataByCategory = (
  data: ExcelExportData[]
): { [category: string]: ExcelExportData[] } => {
  const grouped: { [category: string]: ExcelExportData[] } = {};

  data.forEach((item) => {
    const category = item["Категория"] || "Общая";
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(item);
  });

  return grouped;
};

// Функция для создания строки заголовка категории
const createCategoryHeader = (
  categoryName: string,
  itemsCount: number
): ExcelExportData => {
  return {
    Артикул: "",
    Название: `${categoryName} (${itemsCount} товаров)`,
    Опт: "",
    "Опт-Мастер": "",
  };
};

// Функция для создания строки разделителя
const createSeparatorRow = (): ExcelExportData => {
  return {
    Артикул: "",
    Название: "",
    Опт: "",
    "Опт-Мастер": "",
  };
};

// Функция для создания сводной статистики по категориям - УДАЛЕНА
// const createCategorySummary = ... - больше не нужна

// Функция для определения типа строки
const getRowType = (
  item: ExcelExportData
): "category" | "separator" | "data" => {
  const nameValue = item["Название"];

  if (!item["Артикул"] && !nameValue && !item["Опт"] && !item["Опт-Мастер"]) {
    return "separator";
  }

  if (
    typeof nameValue === "string" &&
    nameValue.includes("товаров)") &&
    !item["Артикул"] &&
    !item["Опт"]
  ) {
    return "category";
  }

  return "data";
};

// Основная функция экспорта
export async function exportToExcel(
  data: any[],
  options: ExportOptions = {}
): Promise<void> {
  const {
    filename = "price_list",
    sheetName = "Прайс-лист",
    includeHeaders = true,
    groupByCategory = true,
  } = options;

  const workbook = new ExcelJS.Workbook();

  // Устанавливаем метаданные документа
  workbook.creator = "Skynet Service";
  workbook.created = new Date();
  workbook.modified = new Date();

  const worksheet = workbook.addWorksheet(sheetName);

  let exportData = [...data];

  // Если нужна группировка по категориям
  if (groupByCategory && data.length > 0) {
    const grouped = groupDataByCategory(data);

    // Сортируем категории: сначала все кроме "Общая", потом "Общая"
    const categoriesWithoutGeneral = Object.keys(grouped)
      .filter((cat) => cat !== "Общая" && grouped[cat].length > 0)
      .sort((a, b) => a.localeCompare(b, "ru"));

    const generalCategory =
      grouped["Общая"] && grouped["Общая"].length > 0 ? ["Общая"] : [];
    const sortedCategories = [...categoriesWithoutGeneral, ...generalCategory];

    exportData = [];

    sortedCategories.forEach((categoryName, index) => {
      const categoryItems = grouped[categoryName];

      // Проверяем, что в категории есть товары
      if (categoryItems && categoryItems.length > 0) {
        // Добавляем заголовок категории
        exportData.push(
          createCategoryHeader(categoryName, categoryItems.length)
        );

        // Добавляем товары категории (сортируем по названию)
        const sortedItems = categoryItems.sort((a, b) =>
          (a["Название"] || "").localeCompare(b["Название"] || "", "ru")
        );
        exportData.push(...sortedItems);

        // Добавляем разделитель между категориями (кроме последней)
        if (index < sortedCategories.length - 1) {
          exportData.push(createSeparatorRow());
        }
      }
    });
  }

  // Определяем колонки: Артикул, Название, Опт, Опт-Мастер
  const columns = [
    { header: "Артикул", key: "Артикул", width: 20 },
    { header: "Название", key: "Название", width: 50 },
    { header: "Опт", key: "Опт", width: 15 },
    { header: "Опт-Мастер", key: "Опт-Мастер", width: 15 },
  ];

  worksheet.columns = columns;

  // Стилизация заголовков таблицы
  if (includeHeaders && exportData.length > 0) {
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF00A6E6" },
    };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };
    headerRow.height = 30;
  }

  // Добавляем данные и применяем стилизацию
  exportData.forEach((item, index) => {
    const rowNumber = worksheet.addRow(item).number;
    const row = worksheet.getRow(rowNumber);
    const rowType = getRowType(item);

    switch (rowType) {
      case "category":
        // Стилизация заголовка категории
        row.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } }; // Белый текст
        row.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF0066CC" }, // Синий фон
        };
        row.alignment = { horizontal: "left", vertical: "middle" }; // По левому краю
        row.height = 30;

        // Объединяем ячейки в заголовке категории
        worksheet.mergeCells(rowNumber, 1, rowNumber, columns.length);

        // Явно устанавливаем значение в первую ячейку объединенного диапазона
        const mergedCell = worksheet.getCell(rowNumber, 1);
        mergedCell.value = item["Название"]; // Название категории
        mergedCell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
        mergedCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF0066CC" },
        };
        mergedCell.alignment = { horizontal: "left", vertical: "middle" };
        break;

      case "separator":
        // Стилизация разделителей
        row.height = 8;
        break;

      case "data":
        // Стилизация обычных строк данных
        row.height = 22;
        break;
    }
  });

  // Применяем общую стилизацию для всех строк
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber > 1) {
      // Пропускаем заголовок таблицы
      const rowType = getRowType(exportData[rowNumber - 2] || {});

      row.eachCell((cell, colNumber) => {
        // Выравнивание и форматирование для ценовых колонок (3-я и 4-я колонки)
        if ((colNumber === 3 || colNumber === 4) && rowType === "data") {
          cell.alignment = { horizontal: "center", vertical: "middle" };
          if (typeof cell.value === "number" && cell.value > 0) {
            cell.numFmt = "#,##0.00";
          }
        }

        // Выравнивание для колонки с артикулом (1-я колонка)
        if (colNumber === 1 && rowType === "data") {
          cell.alignment = { horizontal: "center", vertical: "middle" };
        }

        // Выравнивание для колонки с названием (2-я колонка)
        if (colNumber === 2 && rowType === "data") {
          cell.alignment = { horizontal: "left", vertical: "middle" };
        }
      });

      // Чередование цветов строк только для обычных данных
      if (rowType === "data") {
        // Подсчитываем только строки с данными для правильного чередования
        const dataRowIndex = exportData
          .slice(0, rowNumber - 2)
          .filter((item) => getRowType(item) === "data").length;

        if (dataRowIndex % 2 === 0) {
          row.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF9F9F9" },
          };
        }
      }
    }
  });

  // Автофильтр только для заголовков таблицы
  if (includeHeaders && exportData.length > 0) {
    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: columns.length },
    };
  }

  // Закрепляем заголовок таблицы
  worksheet.views = [
    {
      state: "frozen",
      ySplit: 1,
      activeCell: "A2",
      showGridLines: true,
    },
  ];

  // Генерация и сохранение файла
  try {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const date = new Date().toLocaleDateString("ru-RU").replace(/\./g, "-");
    const categoryPrefix = groupByCategory ? "_by_categories" : "";
    const finalFilename = `${filename}${categoryPrefix}_${date}.xlsx`;

    saveAs(blob, finalFilename);
  } catch (error) {
    console.error("Ошибка при создании Excel файла:", error);
    throw new Error("Не удалось создать Excel файл. Попробуйте еще раз.");
  }
}
